import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import axios from "axios";
import apiUrls from "../../../../../api";
import { Tooltip } from "../../../../../common/components/Tooltip/Tooltip";

interface YesNoQuestionData {
  question_text: string;
  is_yes_good_answer: boolean;
  yes_button_text: string;
  no_button_text: string;
  explanation_true_text?: string;
  explanation_false_text?: string;
}

interface YesNoQuestionDetailProps {
  score: number; // 0-100, used to determine if user answered correctly
  contentId?: number;
}

const YesNoQuestionDetail = ({ score, contentId }: YesNoQuestionDetailProps) => {
  const { t } = useTranslation();
  const [questionData, setQuestionData] = useState<YesNoQuestionData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const fetchedContentIdRef = useRef<number | null>(null);

  // Fetch content-yes-no-question data
  useEffect(() => {
    const fetchData = async () => {
      if (!contentId) return;

      // Prevent duplicate fetches
      if (fetchedContentIdRef.current === contentId) return;
      fetchedContentIdRef.current = contentId;

      setIsLoading(true);

      try {
        const response = await axios.get(
          `${apiUrls.stats.contentSpecificData}?contentId=${contentId}&contentType=content-yes-no-question`,
        );

        // Note: axios interceptor in auth.ts auto-unwraps response.data.data to response.data
        const data = response.data;

        if (data) {
          const yesText = data.yes_button_text;
          const noText = data.no_button_text;

          setQuestionData({
            question_text: data.question_text,
            is_yes_good_answer: data.is_yes_good_answer,
            // Use API text only if it's custom (not generic "Yes"/"No")
            yes_button_text: yesText && yesText.toLowerCase() !== "yes" ? yesText : "",
            no_button_text: noText && noText.toLowerCase() !== "no" ? noText : "",
            explanation_true_text: data.explanation_true_text,
            explanation_false_text: data.explanation_false_text,
          });
        }
      } catch (err) {
        console.error("Error fetching yes/no question content:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [contentId]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600" />
      </div>
    );
  }

  if (!questionData) {
    return <div className="text-center py-8 text-gray-500">{t("statsNew.interaction.noData")}</div>;
  }

  // Determine user's answer based on score and is_yes_good_answer
  // Score is 0 or 1 (1 = correct)
  const userAnsweredCorrectly = score === 1;
  const correctAnswerIsYes = questionData.is_yes_good_answer === true;

  // If user was correct and correct answer is Yes → user selected Yes
  // If user was correct and correct answer is No → user selected No
  // If user was wrong and correct answer is Yes → user selected No
  // If user was wrong and correct answer is No → user selected Yes
  const userSelectedYes = userAnsweredCorrectly ? correctAnswerIsYes : !correctAnswerIsYes;

  // Determine status for each button
  const yesIsCorrect = correctAnswerIsYes;
  const noIsCorrect = !correctAnswerIsYes;

  const yesWasSelected = userSelectedYes;
  const noWasSelected = !userSelectedYes;

  // Get the appropriate explanation
  const explanation = (
    userAnsweredCorrectly ? questionData.explanation_true_text : questionData.explanation_false_text
  )?.trim();

  return (
    <div className="h-full flex flex-col">
      <div className="border-2 border-teal-200 rounded-lg p-4 flex-1 overflow-y-auto">
        {/* Question */}
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          {/* Question header */}
          <div className="bg-gray-50 px-4 py-3 border-b border-gray-200">
            <span className="text-sm font-medium text-gray-500">Question</span>
            <p className="mt-2 font-medium text-gray-900">{questionData.question_text}</p>
          </div>

          {/* Answers */}
          <div className="p-4 space-y-2">
            {/* Yes button */}
            <AnswerRow
              text={questionData.yes_button_text || t("statsNew.interaction.yesNo.yes")}
              isCorrect={yesIsCorrect}
              wasSelected={yesWasSelected}
              t={t}
            />

            {/* No button */}
            <AnswerRow
              text={questionData.no_button_text || t("statsNew.interaction.yesNo.no")}
              isCorrect={noIsCorrect}
              wasSelected={noWasSelected}
              t={t}
            />
          </div>

          {/* Explanation */}
          {explanation && (
            <div className="px-4 pb-4">
              <p className="text-xs font-medium text-gray-500 mb-1">
                {t("statsNew.interaction.explanation")}
              </p>
              <div
                className={`p-3 rounded-lg ${userAnsweredCorrectly ? "bg-green-50" : "bg-red-50"}`}
              >
                <p
                  className={`text-sm ${userAnsweredCorrectly ? "text-green-700" : "text-red-700"}`}
                >
                  {explanation}
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

interface AnswerRowProps {
  text: string;
  isCorrect: boolean;
  wasSelected: boolean;
  t: (key: string) => string;
}

const AnswerRow = ({ text, isCorrect, wasSelected, t }: AnswerRowProps) => {
  const isCorrectAnswer = isCorrect && wasSelected;
  const isWrongAnswer = !isCorrect && wasSelected;
  const isMissedCorrect = isCorrect && !wasSelected;

  let bgColor = "bg-white border-gray-200";
  let textColor = "text-gray-700";

  if (isCorrectAnswer) {
    bgColor = "bg-green-100 border-green-500 border-2";
    textColor = "text-green-800 font-medium";
  } else if (isWrongAnswer) {
    bgColor = "bg-red-100 border-red-400";
    textColor = "text-red-800";
  } else if (isMissedCorrect) {
    bgColor = "bg-amber-50 border-amber-300 border-dashed";
    textColor = "text-amber-700";
  }

  return (
    <div className={`flex items-center gap-3 px-4 py-3 rounded-lg border ${bgColor}`}>
      <div className="flex-shrink-0">
        {isCorrectAnswer && (
          <Tooltip message={t("statsNew.interaction.quiz.tooltipCorrect")}>
            <svg
              className="h-5 w-5 text-green-600 cursor-help"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M5 13l4 4L19 7"
              />
            </svg>
          </Tooltip>
        )}
        {isWrongAnswer && (
          <Tooltip message={t("statsNew.interaction.quiz.tooltipWrong")}>
            <svg
              className="h-5 w-5 text-red-600 cursor-help"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </Tooltip>
        )}
        {isMissedCorrect && (
          <Tooltip message={t("statsNew.interaction.quiz.tooltipMissed")}>
            <svg
              className="h-5 w-5 text-amber-500 cursor-help"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
              />
            </svg>
          </Tooltip>
        )}
        {!wasSelected && !isCorrect && <div className="w-5 h-5" />}
      </div>
      <span className={`text-sm ${textColor}`}>{text}</span>
    </div>
  );
};

export default YesNoQuestionDetail;
